import { CSSProperties, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  Input,
  InputBase,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { FormError, SelectIcon } from './Form';
import { SelectOption } from './SelectOption';

import inputStyles from '../input/input.module.scss';
import dropdownStyles from './dropdown.module.scss';

export type SelectionValue = string | boolean | null;

export type SingleSelectProps<Data = unknown> = {
  className?: string;
  clearable?: boolean;
  data: Array<{ label: string; value: Data }>;
  /** initial value (uncontrolled) */
  defaultValue?: Data;
  disabled?: boolean;
  error?: string | boolean;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  /** called for each change in selection */
  onChange?: (value: Data | null) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  role?: string;
  searchable?: boolean;
  style?: CSSProperties;
  title?: string;
  /** selection value (controlled) */
  value: Data | null;
  /** max height of dropdown scrollcontainer */
  dropdownHeight?: number | string;
  withinPortal?: boolean;
  variant?: 'regular' | 'ghost';
  tableLayout?: boolean;
};

function getCurrentItem<Data>(
  data: SingleSelectProps<Data>['data'],
  search: string
) {
  return data.find(
    (item) =>
      item.value === search ||
      String(item.value) === search ||
      item.label === search
  );
}

export function SingleSelect<Data>({
  className,
  clearable = true,
  data,
  defaultValue,
  disabled = false,
  dropdownHeight = '40vh',
  error,
  inlineError = false,
  label,
  loading = false,
  onChange,
  placeholder,
  readOnly = false,
  required,
  role = 'select',
  searchable = true,
  style,
  tableLayout,
  title,
  value,
  variant = 'regular',
  withinPortal = true,
}: SingleSelectProps<Data>) {
  const id = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });
  const currentItem = getCurrentItem(data, String(selection));
  const [shouldFilterOptions, setShouldFilterOptions] = useState(false);
  const [search, setSearch] = useState(
    currentItem ? currentItem.label || currentItem.value : ''
  );
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownOpen() {
      const currentItem = getCurrentItem(data, String(selection));
      if (currentItem) {
        setSearch(currentItem.label ?? currentItem.value);
      } else {
        setSearch('');
      }
      setShouldFilterOptions(false);
    },
  });

  const filteredOptions = shouldFilterOptions
    ? data.filter((item) =>
        (item.label || String(item.value))
          .toLowerCase()
          .includes(String(search).toLowerCase().trim())
      )
    : data;

  const options = filteredOptions.map((item) => (
    <SelectOption
      key={String(item.value)}
      value={String(item.value)}
      label={item.label || String(item.value)}
      selected={selection === item.value}
      variant={variant === 'ghost' ? 'primary' : undefined}
    />
  ));

  let currentInputValue = '';
  if (combobox.dropdownOpened) {
    currentInputValue = String(search);
  } else if (selection != null && currentItem) {
    currentInputValue = currentItem?.label ?? currentItem?.value;
  }

  const clearButton = (
    <ClearButton
      disabled={disabled || readOnly}
      loading={loading}
      onClear={() => {
        if (selection != null) {
          setSelection(null);
        }
      }}
    />
  );

  const selectIcon = (
    <SelectIcon
      disabled={loading || disabled || readOnly}
      error={!!error}
      variant={variant === 'ghost' ? 'primary' : undefined}
    />
  );

  let largeRightSection = false;
  let rightSection: ReactNode | undefined = selectIcon;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="loading-indicator" />;
  } else if (selection != null && clearable && !disabled) {
    largeRightSection = true;
    rightSection = (
      <>
        {clearButton}
        {selectIcon}
      </>
    );
  }

  const sharedInputProps = {
    'data-testid': 'single-select',
    classNames: {
      error: inputStyles['error'],
      input: clsx(
        inputStyles['input'],
        variant === 'ghost' && inputStyles['input--primary'],
        error && inputStyles['input--error'],
        loading && inputStyles['input--is-loading']
      ),
      section: inputStyles['section'],
      wrapper: clsx(
        inputStyles['wrapper'],
        largeRightSection && inputStyles['wrapper--large-right-section']
      ),
    },
    disabled: loading || disabled,
    readOnly: readOnly,
    error: error && (
      <FormError
        error={typeof error === 'string' ? error : undefined}
        inline={inlineError}
      />
    ),
    id,
    onClick: () => combobox.openDropdown(),
    onFocus: () => combobox.openDropdown(),
    onBlur: () => combobox.closeDropdown(),
    placeholder,
    rightSection,
    unstyled: true,
  };

  return (
    <div className={className} title={title} role={role} ref={dropdownRef}>
      <Combobox
        store={combobox}
        disabled={disabled || loading || readOnly}
        readOnly={readOnly}
        withinPortal={withinPortal}
        classNames={{
          dropdown: dropdownStyles['dropdown'],
        }}
        onOptionSubmit={(itemValue) => {
          const newItem = getCurrentItem(data, itemValue);
          if (newItem && newItem.value !== currentItem?.value) {
            setSelection(newItem.value);
            setSearch(newItem.label || newItem.value);
          }
          combobox.closeDropdown();
          combobox.targetRef.current?.blur();
        }}
      >
        <div
          className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}
        >
          {label && (
            <FormLabel
              disabled={disabled}
              error={error}
              id={id}
              inline={inlineError}
              label={label}
              loading={loading}
              required={required}
            />
          )}
          <Combobox.Target>
            {searchable ? (
              <TextInput
                {...sharedInputProps}
                rightSectionPointerEvents="none"
                value={currentInputValue}
                onChange={(event) => {
                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                  setShouldFilterOptions(true);
                  setSearch(event.currentTarget.value);
                }}
              />
            ) : (
              <InputBase
                {...sharedInputProps}
                rightSectionPointerEvents="none"
                component="button"
                type="button"
              >
                {currentItem?.label ??
                  (currentItem?.value
                    ? String(currentItem?.value)
                    : undefined) ?? (
                    <Input.Placeholder>{placeholder}</Input.Placeholder>
                  )}
              </InputBase>
            )}
          </Combobox.Target>
        </div>

        <Combobox.Dropdown>
          <Combobox.Options
            mah={dropdownHeight}
            style={{ overflowY: 'auto', width: style?.width }}
          >
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>
                {search !== ''
                  ? t('app.select.noOptionsFound', { query: search })
                  : t('app.select.noOptionsAvailable')}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
